import type {
  AdminAccessView,
  AdminAuditView,
  AdminCreditGrant,
  AdminMessage,
  BroadcastPurpose,
  AdminPostPatch,
  AdminQuoteDetail,
  AdminQuoteListQuery,
  AdminQuoteWaitlistSummary,
  AdminQuoteView,
  AdminSimulationListQuery,
  Axis,
  AdminStats,
  AdminSummary,
  AdminUserSimulation,
  GoldenCoverageRow,
  PriceCacheEntry,
  AppSettings,
  GoldenMarket,
  GoldenProduct,
  GoldenProductInput,
  GoldenProductPatch,
  GoldenSetGenerateInput,
  AdminEntry,
  PageQuery,
  AdminUserView,
  Entitlements,
  MenuTags,
  PostProduct,
  PostSource,
  QuoteResult,
  QuoteRequestStatus,
  QuoteStatus,
  SimulationEvaluation,
  SimulationPublic,
  SimulationStatus,
} from '@repo/shared'
import { env } from './env'

import type { GetToken } from './lib/auth'

/** 새 화면의 API 호출은 이 함수를 가져다 따로 파일에 둔다 (예: cases-api.ts). 이 파일의 충돌을 줄이려는 것 */
export async function request<T>(
  getToken: GetToken,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = await getToken()
  const res = await fetch(`${env.API_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null
    throw new Error(adminErrorMessage(res.status, body?.error?.message))
  }
  return (await res.json()) as T
}

/** 서버가 영어로 주는 문구 중 관리자 화면에 뜨는 것. 고객 앱과 같이 쓰는 라우트라 서버 문구는 두고 여기서 바꾼다 */
const KNOWN_MESSAGES: Record<string, string> = {
  'Local factory integration is disabled': '공장 연결이 꺼져 있어요',
  'Local factory integration is not configured': '공장 연결이 꺼져 있어요',
  'Quote is closed': '이미 닫힌 견적이에요',
}

const STATUS_MESSAGES: Record<number, string> = {
  400: '입력값을 다시 확인해 주세요',
  401: '다시 로그인해 주세요',
  403: '권한이 없어요',
  404: '찾을 수 없어요. 이미 지워졌을 수 있어요',
  409: '이미 바뀌었어요. 새로고침해 주세요',
  429: '요청이 너무 많아요. 잠시 뒤 다시 해 주세요',
  503: '지금은 쓸 수 없어요. 잠시 뒤 다시 해 주세요',
}

/** 관리자 화면은 한국어만 쓴다: 한글 문구는 그대로, 아는 영어 문구는 번역, 나머지는 상태별 문구 */
function adminErrorMessage(status: number, message: string | undefined): string {
  if (message && /[가-힣]/.test(message)) return message
  if (message && KNOWN_MESSAGES[message]) return KNOWN_MESSAGES[message]
  return STATUS_MESSAGES[status] ?? `요청이 실패했어요 (${status}). 잠시 뒤 다시 해 주세요`
}

export const json = (method: 'POST' | 'PUT' | 'PATCH', body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
})

const pageQs = ({ page, perPage }: PageQuery) => `page=${page}&perPage=${perPage}`

/** 오프셋 페이지네이션 응답 */
export interface Page<T> {
  items: T[]
  total: number
  page: number
  perPage: number
}

export const adminApi = {
  /** access 는 C52 에서 더한 칸이라 옛 서버면 없다 */
  me: (getToken: GetToken) =>
    request<{ userId: string; environment: string; access?: AdminAccessView }>(
      getToken,
      '/admin/me',
    ),
  getSettings: (getToken: GetToken) =>
    request<{ settings: AppSettings }>(getToken, '/admin/settings'),
  putSettings: (getToken: GetToken, settings: AppSettings) =>
    request<{ settings: AppSettings }>(getToken, '/admin/settings', json('PUT', settings)),
  /** 시장별 견적 대기 명단 회원 수 (로드맵 P7). 옛 API 에는 없어 404 다 */
  quoteWaitlist: (getToken: GetToken) =>
    request<AdminQuoteWaitlistSummary>(getToken, '/admin/quote-waitlist'),

  goldenSet: {
    list: (getToken: GetToken, filter: { categoryId?: string; market?: GoldenMarket } = {}) => {
      const qs = new URLSearchParams()
      if (filter.categoryId) qs.set('categoryId', filter.categoryId)
      if (filter.market) qs.set('market', filter.market)
      const suffix = qs.size ? `?${qs.toString()}` : ''
      return request<{ items: GoldenProduct[] }>(getToken, `/admin/golden-set${suffix}`)
    },
    create: (getToken: GetToken, input: GoldenProductInput) =>
      request<{ item: GoldenProduct }>(getToken, '/admin/golden-set', json('POST', input)),
    generate: (getToken: GetToken, input: GoldenSetGenerateInput) =>
      request<{ items: GoldenProduct[]; skipped: number }>(
        getToken,
        '/admin/golden-set/generate',
        json('POST', input),
      ),
    update: (getToken: GetToken, id: string, patch: GoldenProductPatch) =>
      request<{ item: GoldenProduct }>(getToken, `/admin/golden-set/${id}`, json('PATCH', patch)),
    remove: (getToken: GetToken, id: string) =>
      request<{ id: string }>(getToken, `/admin/golden-set/${id}`, { method: 'DELETE' }),
  },

  community: {
    list: (getToken: GetToken, q: PageQuery) =>
      request<Page<AdminPost>>(getToken, `/admin/community/posts?${pageQs(q)}`),
    remove: (getToken: GetToken, id: string) =>
      request<{ ok: true }>(getToken, `/admin/community/posts/${id}`, { method: 'DELETE' }),
    patch: (getToken: GetToken, id: string, patch: AdminPostPatch) =>
      request<{ post: unknown }>(getToken, `/admin/community/posts/${id}`, json('PATCH', patch)),
    /** 글 하나의 댓글 (삭제된 것 포함) */
    comments: (getToken: GetToken, postId: string) =>
      request<{ items: AdminComment[] }>(getToken, `/admin/community/posts/${postId}/comments`),
    removeComment: (getToken: GetToken, id: string) =>
      request<{ ok: true }>(getToken, `/admin/community/comments/${id}`, { method: 'DELETE' }),
    /** 작성자 커뮤니티 이용 제한(글·댓글 쓰기 금지) / 해제 */
    restrict: (getToken: GetToken, userId: string, restricted: boolean) =>
      request<{ restricted: boolean }>(
        getToken,
        `/admin/community/users/${encodeURIComponent(userId)}/${restricted ? 'restrict' : 'unrestrict'}`,
        { method: 'POST' },
      ),
  },

  quotes: {
    list: (getToken: GetToken, q: AdminQuoteListQuery = {}) => {
      const qs = new URLSearchParams()
      if (q.status) qs.set('status', q.status)
      if (q.overdue) qs.set('overdue', '1')
      if (q.q) qs.set('q', q.q)
      return request<{ items: AdminQuoteView[] }>(
        getToken,
        `/admin/quotes${qs.size ? `?${qs.toString()}` : ''}`,
      )
    },
    /** 요청 1건 + 공장 견적 내역 */
    get: (getToken: GetToken, id: string) =>
      request<AdminQuoteDetail>(getToken, `/admin/quotes/${encodeURIComponent(id)}`),
    setNotes: (getToken: GetToken, id: string, adminNotes: string) =>
      request<{ quote: AdminQuoteView }>(
        getToken,
        `/admin/quotes/${id}/notes`,
        json('PATCH', { adminNotes }),
      ),
    message: (getToken: GetToken, id: string, msg: AdminMessage) =>
      request<{ ok: true }>(getToken, `/admin/quotes/${id}/message`, json('POST', msg)),
    addOffer: (getToken: GetToken, id: string, offer: QuoteResult) =>
      request<AdminQuoteDetail>(getToken, `/admin/quotes/${id}/offers`, json('POST', offer)),
    updateOffer: (getToken: GetToken, id: string, offerId: string, offer: QuoteResult) =>
      request<AdminQuoteDetail>(
        getToken,
        `/admin/quotes/${id}/offers/${offerId}`,
        json('PUT', offer),
      ),
    removeOffer: (getToken: GetToken, id: string, offerId: string) =>
      request<AdminQuoteDetail>(getToken, `/admin/quotes/${id}/offers/${offerId}`, {
        method: 'DELETE',
      }),
    /** reason 은 작업 기록에 전후 상태와 함께 남는다. 마감은 선택, 다시 열기는 서버가 필수로 본다 (C04) */
    setStatus: (
      getToken: GetToken,
      id: string,
      status: QuoteStatus | QuoteRequestStatus,
      reason?: string,
    ) =>
      request<{ quote: AdminQuoteView }>(
        getToken,
        `/admin/quotes/${id}`,
        json('PATCH', { status, ...(reason?.trim() ? { reason: reason.trim() } : {}) }),
      ),
  },

  users: {
    lookup: (getToken: GetToken, email: string) =>
      request<{ items: AdminUserView[] }>(
        getToken,
        `/admin/users/lookup?email=${encodeURIComponent(email)}`,
      ),
    entitlements: (getToken: GetToken, userId: string) =>
      request<{ userId: string; entitlements: Entitlements }>(
        getToken,
        `/admin/users/${userId}/plan`,
      ),
    byId: (getToken: GetToken, userId: string) =>
      request<{ user: AdminUserView }>(
        getToken,
        `/admin/users/by-id/${encodeURIComponent(userId)}`,
      ),
    simulations: (getToken: GetToken, userId: string) =>
      request<{ items: AdminUserSimulation[] }>(
        getToken,
        `/admin/users/${encodeURIComponent(userId)}/simulations`,
      ),
    grantCredits: (getToken: GetToken, userId: string, grant: AdminCreditGrant) =>
      request<{ entitlements: Entitlements }>(
        getToken,
        `/admin/users/${encodeURIComponent(userId)}/credits`,
        json('POST', grant),
      ),
    revokeCredits: (getToken: GetToken, userId: string, kind: AdminCreditGrant['kind']) =>
      request<{ removed: number; entitlements: Entitlements }>(
        getToken,
        `/admin/users/${encodeURIComponent(userId)}/credits`,
        { method: 'DELETE', body: JSON.stringify({ kind }) },
      ),
    message: (getToken: GetToken, userId: string, msg: AdminMessage) =>
      request<{ ok: true }>(
        getToken,
        `/admin/users/${encodeURIComponent(userId)}/message`,
        json('POST', msg),
      ),
    /** 끝나지 않으면 503 ACCOUNT_DELETE_INCOMPLETE — 다시 부르면 이어서 지운다. warnings: 'revenuecat' = 구독자 기록이 남음 */
    remove: (getToken: GetToken, userId: string) =>
      request<{ ok: true; warnings?: string[] }>(
        getToken,
        `/admin/users/${encodeURIComponent(userId)}`,
        {
          method: 'DELETE',
        },
      ),
    admins: (getToken: GetToken) => request<{ items: AdminEntry[] }>(getToken, '/admin/admins'),
    addAdmin: (getToken: GetToken, email: string) =>
      request<{ items: AdminEntry[] }>(getToken, '/admin/admins', json('POST', { email })),
    removeAdmin: (getToken: GetToken, email: string) =>
      request<{ items: AdminEntry[] }>(getToken, `/admin/admins/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      }),
  },

  simulations: {
    list: (getToken: GetToken, q: Partial<AdminSimulationListQuery> & PageQuery) => {
      const qs = new URLSearchParams({ page: String(q.page), perPage: String(q.perPage) })
      for (const k of [
        'status',
        'gate',
        'categoryId',
        'language',
        'userId',
        'check',
        'q',
      ] as const) {
        const v = q[k]
        if (v) qs.set(k, v)
      }
      return request<Page<SimulationListItem>>(getToken, `/admin/simulations?${qs.toString()}`)
    },
    rerun: (getToken: GetToken, id: string) =>
      request<{ id: string }>(getToken, `/admin/simulations/${id}/rerun`, { method: 'POST' }),
    get: (getToken: GetToken, id: string) =>
      request<{ simulation: SimulationDetail }>(getToken, `/admin/simulations/${id}`),
    workflow: (getToken: GetToken, id: string) =>
      request<GenerationWorkflowStatus>(getToken, `/admin/simulations/${id}/workflow`),
    recover: (getToken: GetToken, id: string) =>
      request<GenerationRecoveryResult>(getToken, `/admin/simulations/${id}/recover`, {
        method: 'POST',
      }),
  },
  summary: (getToken: GetToken) => request<AdminSummary>(getToken, '/admin/summary'),
  stats: (getToken: GetToken, days: number) =>
    request<AdminStats>(getToken, `/admin/stats?days=${days}`),
  goldenCoverage: (getToken: GetToken) =>
    request<{ items: GoldenCoverageRow[] }>(getToken, '/admin/golden-set/coverage'),
  prices: {
    list: (getToken: GetToken, q: string) =>
      request<{ items: PriceCacheEntry[]; total: number }>(
        getToken,
        `/admin/prices${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      ),
    remove: (getToken: GetToken, name: string) =>
      request<{ ok: true }>(getToken, `/admin/prices/${encodeURIComponent(name)}`, {
        method: 'DELETE',
      }),
    seed: (getToken: GetToken) =>
      request<{ started: true }>(getToken, '/admin/prices/seed', { method: 'POST' }),
  },
  audit: (getToken: GetToken, q: PageQuery) =>
    request<Page<AdminAuditView>>(getToken, `/admin/audit?${pageQs(q)}`),
  broadcast: {
    recipients: (getToken: GetToken) =>
      request<{ count: number }>(getToken, '/admin/broadcast/recipients'),
    send: (
      getToken: GetToken,
      msg: AdminMessage & { expectedRecipients: number; purpose: BroadcastPurpose },
    ) => request<{ queued: number }>(getToken, '/admin/broadcast', json('POST', msg)),
  },
}

export interface AdminPost {
  id: string
  userId: string
  authorName: string
  simulationId: string | null
  categoryId: string | null
  question: string
  body: string
  images: string[]
  pollTitle: string
  language: string
  product: PostProduct | null
  /** 홈 추천 시각 (null = 추천 아님) */
  featuredAt: string | null
  isDeleted: boolean
  createdAt: string
  totalVotes: number
  commentCount: number
  /** 앱 사용자의 신고 건수 (빈도 제한으로 건너뛴 신고는 빠진다) */
  reportCount: number
  /** 작성자가 커뮤니티 이용 제한 중인지 */
  authorRestricted: boolean
  /** 비어 있으면 투표 없는 일반 글 */
  options: string[]
  /** 고민 유형 id (최대 3). 0026 전 서버는 칸이 없다 */
  concernTags?: string[]
  /** 글의 메뉴 꼬리표. 0026 전 서버는 칸이 없다 */
  menuTags?: MenuTags
  /** user = 사용자 글, example = 운영팀 예시 고민. 0026 전 서버는 칸이 없다 */
  source?: PostSource
}

/** 어드민 커뮤니티 › 댓글 보기 */
export interface AdminComment {
  id: string
  postId: string
  userId: string
  authorName: string
  body: string
  isDeleted: boolean
  createdAt: string
  reportCount: number
  authorRestricted: boolean
}

export interface SimulationListItem {
  id: string
  userId: string | null
  deviceId: string | null
  categoryId: string
  language: 'ko' | 'en'
  market: 'kr' | 'us'
  status: SimulationStatus
  productName: string | null
  evaluation: SimulationEvaluation | null
  error: string | null
  isDeleted: boolean
  createdAt: string
  updatedAt: string
}

export interface SimulationDetail extends SimulationListItem {
  input: {
    categoryId: string
    language: string
    mode?: 'create' | 'analyze'
    axis?: Axis
    majorId?: string | null
    subId?: string | null
    images?: string[]
    messages: { text: string; mediaCount: number }[]
    /** 메뉴 꼬리표 (메뉴 축만, 고른 것이 있을 때만 저장된다) */
    menuTags?: MenuTags
  }
  result: SimulationPublic | null
  internal: Record<string, unknown> | null
  expiresAt: string | null
}

export interface GenerationWorkflowStatus {
  simulationStatus: SimulationStatus
  linkage: 'stored' | 'legacy-initial' | 'unavailable'
  attempt: number | null
  workflowStatus:
    | 'queued'
    | 'running'
    | 'paused'
    | 'errored'
    | 'terminated'
    | 'complete'
    | 'waiting'
    | 'waitingForPause'
    | 'unknown'
    | null
  recoverable: boolean
}

export interface GenerationRecoveryResult {
  status: 'done' | 'failed'
  refunded: boolean
  workflowStatus: 'errored' | 'terminated'
}
